// asText — coerce any payload value to a safe display string.
//
// Phase-4 (Claude) synthesis output occasionally emits a value as a rich OBJECT or
// ARRAY-OF-OBJECTS where the contract/type expects a plain string — e.g. a journey
// "moment" as { name, sentiment, description, finding_keys }, or a workflow "finding"
// as { finding, keys }. Rendering such a value directly as a JSX child crashes React
// with "Objects are not valid as a React child". Wrapping every generated render spot
// in asText() makes the tabs resilient to whatever shape the model produces, without
// changing anything for the normal string case.
export function asText(x: any): string {
  if (x == null) return '';
  if (typeof x === 'string') return x;
  if (typeof x === 'number' || typeof x === 'boolean') return String(x);
  if (Array.isArray(x)) return x.map(asText).filter(Boolean).join(', ');
  if (typeof x === 'object') {
    return (
      x.name || x.label || x.title || x.text || x.finding ||
      x.description || x.value || ''
    );
  }
  return String(x);
}

// A theme label used to be truncated to ~57 chars + "…" by the synthesis
// assembler (fixed there going forward). Older stored payloads still carry the
// trailing ellipsis, which renders as a broken, cut-off title. Strip any
// trailing "…" / "..." (plus dangling punctuation) so old and new payloads both
// read as a clean label. Never adds an ellipsis — only removes one.
export function cleanLabel(x: any): string {
  return asText(x).replace(/\s*(?:…|\.{3})\s*$/, '').replace(/[\s,;:—-]+$/, '').trim();
}

// Truncate to fit a small fixed slot (e.g. a chip) WITHOUT cutting a word in half:
// trim back to the last whole word and add " …". Cleans any pre-existing ellipsis
// first so we never stack "……".
export function clipWords(x: any, max: number): string {
  const s = cleanLabel(x);
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  const word = (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:—-]+$/, '');
  return `${word} …`;
}

// Word-wrap a label into up to `maxLines` lines of at most `maxChars` each, for a
// fixed-width SVG node. Wraps on spaces (never mid-word). If the text needs more
// than maxLines, the last allowed line absorbs the remaining words and is
// word-trimmed with a trailing "…" so the node keeps a stable height instead of
// running off the canvas.
export function wrapLines(x: any, maxChars: number, maxLines: number): string[] {
  const words = cleanLabel(x).split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = '';
  for (let i = 0; i < words.length; i++) {
    const w = words[i];
    const next = cur ? `${cur} ${w}` : w;
    if (next.length <= maxChars) { cur = next; continue; }
    // `w` doesn't fit on the current line.
    if (lines.length === maxLines - 1) {
      // On the last allowed line: keep what's built, then ellipsize the rest.
      const rest = words.slice(i).join(' ');
      const room = maxChars - (cur ? cur.length + 1 : 0);
      let tail = rest;
      if (rest.length > Math.max(room, 0)) {
        const cut = rest.slice(0, Math.max(room, 8));
        const ls = cut.lastIndexOf(' ');
        tail = `${(ls > 0 ? cut.slice(0, ls) : cut).replace(/[\s,;:—-]+$/, '')} …`;
      }
      cur = cur ? `${cur} ${tail}` : tail;
      break;
    }
    if (cur) lines.push(cur);
    cur = w;
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}
